import { Component, EventEmitter, Inject, Output } from '@angular/core';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormControl } from '@angular/forms';
import { RecoveryAgent } from '../../../models/recoveryagent.model';
import { map, Observable, startWith } from 'rxjs';
import { toggleAutocompletePanel } from '../../../shared/utils/autocomplete-panel.utils';

export interface RecoveryAgentModalData {
  isEdit: boolean;
  record: Partial<RecoveryAgent>;
  operatingCompanyOptions: { id: number; label: string; name: string }[];
  officeKeyOptions: { key: string; label: string; claimOfficeId: number | null }[];
  stateOptions: { code: string; label: string }[];
}

export type RecoveryAgentModalResult = Partial<RecoveryAgent>;

type OperatingCompanyOption = RecoveryAgentModalData['operatingCompanyOptions'][number];
type OfficeKeyOption = RecoveryAgentModalData['officeKeyOptions'][number];
type StateOption = RecoveryAgentModalData['stateOptions'][number];
type ExternalOrgOption = { value: string; label: string };
type PaperlessIndicatorOption = { value: 'Y' | 'N' | null; label: string };

@Component({
  selector: 'app-recovery-agent-modal',
  standalone: false,
  templateUrl: './recovery-agent-modal.component.html',
  styleUrl: './recovery-agent-modal.component.css',
})
export class RecoveryAgentModalComponent {
  @Output() submitRecord = new EventEmitter<Partial<RecoveryAgent>>();
  formRecord: Partial<RecoveryAgent>;
  validationErrors: string[] = [];
  dialogError = '';
  readonly externalOrgOptions: ExternalOrgOption[] = [
    { value: 'YES', label: 'YES' },
    { value: 'NO', label: 'NO' },
  ];
  readonly paperlessIndicatorOptions: PaperlessIndicatorOption[] = [
    { value: 'Y', label: 'Yes' },
    { value: 'N', label: 'No' },
    { value: null, label: '(blank)' },
  ];

  operatingCompanyControl = new FormControl<string | OperatingCompanyOption>('');
  officeKeyControl = new FormControl<string | OfficeKeyOption>('');
  stateControl = new FormControl<string | StateOption>('');
  externalOrgControl = new FormControl<string | ExternalOrgOption>('');
  paperlessIndicatorControl = new FormControl<string | PaperlessIndicatorOption>('');

  filteredOperatingCompanyOptions$: Observable<OperatingCompanyOption[]>;
  filteredOfficeKeyOptions$: Observable<OfficeKeyOption[]>;
  filteredStateOptions$: Observable<StateOption[]>;
  filteredExternalOrgOptions$: Observable<ExternalOrgOption[]>;
  filteredPaperlessIndicatorOptions$: Observable<PaperlessIndicatorOption[]>;

  constructor(
    public dialogRef: MatDialogRef<RecoveryAgentModalComponent, RecoveryAgentModalResult>,
    @Inject(MAT_DIALOG_DATA) public data: RecoveryAgentModalData
  ) {
    this.formRecord = { ...data.record };
    this.filteredOperatingCompanyOptions$ = this.operatingCompanyControl.valueChanges.pipe(
      startWith(this.operatingCompanyControl.value),
      map((value) => this.filterOperatingCompanies(value))
    );
    this.filteredOfficeKeyOptions$ = this.officeKeyControl.valueChanges.pipe(
      startWith(this.officeKeyControl.value),
      map((value) => this.filterOfficeKeys(value))
    );
    this.filteredStateOptions$ = this.stateControl.valueChanges.pipe(
      startWith(this.stateControl.value),
      map((value) => this.filterStates(value))
    );
    this.filteredExternalOrgOptions$ = this.externalOrgControl.valueChanges.pipe(
      startWith(this.externalOrgControl.value),
      map((value) => this.filterExternalOrgOptions(value))
    );
    this.filteredPaperlessIndicatorOptions$ = this.paperlessIndicatorControl.valueChanges.pipe(
      startWith(this.paperlessIndicatorControl.value),
      map((value) => this.filterPaperlessIndicatorOptions(value))
    );

    this.initializeControlValues();

    if (data.isEdit) {
      this.operatingCompanyControl.disable({ emitEvent: false });
      this.officeKeyControl.disable({ emitEvent: false });
    }
  }

  onOperatingCompanyChange(opCompanyId: unknown): void {
    const selectedId = this.toNullablePositiveNumber(opCompanyId);
    const selectedOption = this.data.operatingCompanyOptions.find(o => o.id === selectedId);
    this.formRecord.OpCompanyID = selectedId ?? undefined;
    this.formRecord.OperatingCompany = selectedOption?.name ?? '';
  }

  onExternalOrgChange(value: string): void {
    if (value === 'NO') {
      this.formRecord.RecoveryAgentTaxID = '205722557';
    } else {
      this.formRecord.RecoveryAgentTaxID = '';
    }
  }

  onOfficeKeyChange(officeKey: unknown): void {
    const selectedKey = typeof officeKey === 'string' ? officeKey : '';
    const selectedOffice = this.data.officeKeyOptions.find(o => o.key === selectedKey);
    this.formRecord.OfficeKey = selectedKey;
    this.formRecord.ClaimOfficeID = selectedOffice?.claimOfficeId ?? null;
  }

  onOperatingCompanyInput(): void {
    if (typeof this.operatingCompanyControl.value === 'string') {
      this.formRecord.OpCompanyID = undefined;
      this.formRecord.OperatingCompany = '';
    }
  }

  onOfficeKeyInput(): void {
    if (typeof this.officeKeyControl.value === 'string') {
      this.formRecord.OfficeKey = '';
      this.formRecord.ClaimOfficeID = null;
    }
  }

  onStateInput(): void {
    if (typeof this.stateControl.value === 'string') {
      this.formRecord.State = '';
    }
  }

  onExternalOrgInput(): void {
    if (typeof this.externalOrgControl.value === 'string') {
      this.formRecord.ExternalOrg = '';
    }
  }

  onPaperlessIndicatorInput(): void {
    if (typeof this.paperlessIndicatorControl.value === 'string') {
      this.formRecord.PaperlessInd = null;
    }
  }

  onOperatingCompanySelected(option: OperatingCompanyOption): void {
    this.operatingCompanyControl.setValue(option);
    this.onOperatingCompanyChange(option.id);
  }

  onOfficeKeySelected(option: OfficeKeyOption): void {
    this.officeKeyControl.setValue(option);
    this.onOfficeKeyChange(option.key);
  }

  onStateSelected(option: StateOption): void {
    this.stateControl.setValue(option);
    this.formRecord.State = option.code;
  }

  onExternalOrgSelected(option: ExternalOrgOption): void {
    this.externalOrgControl.setValue(option);
    this.formRecord.ExternalOrg = option.value;
    this.onExternalOrgChange(option.value);
  }

  onPaperlessIndicatorSelected(option: PaperlessIndicatorOption): void {
    this.paperlessIndicatorControl.setValue(option);
    this.formRecord.PaperlessInd = option.value;
  }

  clearOperatingCompany(event: Event): void {
    event.stopPropagation();
    if (this.data.isEdit) {
      return;
    }

    this.operatingCompanyControl.setValue('');
    this.formRecord.OpCompanyID = undefined;
    this.formRecord.OperatingCompany = '';
  }

  clearOfficeKey(event: Event): void {
    event.stopPropagation();
    if (this.data.isEdit) {
      return;
    }

    this.officeKeyControl.setValue('');
    this.formRecord.OfficeKey = '';
    this.formRecord.ClaimOfficeID = null;
  }

  clearState(event: Event): void {
    event.stopPropagation();
    this.stateControl.setValue('');
    this.formRecord.State = '';
  }

  clearPaperlessIndicator(event: Event): void {
    event.stopPropagation();
    this.paperlessIndicatorControl.setValue('');
    this.formRecord.PaperlessInd = null;
  }

  openOrClosePanel(event: Event, trigger: MatAutocompleteTrigger): void {
    toggleAutocompletePanel(event, trigger);
  }

  displayOperatingCompany(option: OperatingCompanyOption | string | null): string {
    if (!option) return '';
    if (typeof option === 'string') return option;
    return option.label;
  }

  displayOfficeKey(option: OfficeKeyOption | string | null): string {
    if (!option) return '';
    if (typeof option === 'string') return option;
    return option.label;
  }

  displayState(option: StateOption | string | null): string {
    if (!option) return '';
    if (typeof option === 'string') return option;
    return option.label;
  }

  displayExternalOrg(option: ExternalOrgOption | string | null): string {
    if (!option) return '';
    if (typeof option === 'string') return option;
    return option.label;
  }

  displayPaperlessIndicator(option: PaperlessIndicatorOption | string | null): string {
    if (!option) return '';
    if (typeof option === 'string') return option;
    return option.label;
  }

  onZipInput(field: 'ZipCode' | 'ZipPlus4', maxLength: number): void {
    const currentValue = field === 'ZipCode' ? this.formRecord.ZipCode : this.formRecord.ZipPlus4;
    const sanitized = String(currentValue ?? '').replace(/\D/g, '').slice(0, maxLength);
    if (field === 'ZipCode') {
      this.formRecord.ZipCode = sanitized;
    } else {
      this.formRecord.ZipPlus4 = sanitized;
    }
  }

  onSubmit(): void {
    this.validationErrors = this.validate(this.formRecord);
    if (this.validationErrors.length > 0) return;
    this.dialogError = '';
    this.submitRecord.emit(this.formRecord);
  }

  onCancel(): void {
    this.dialogRef.close(undefined);
  }

  private validate(record: Partial<RecoveryAgent>): string[] {
    const errors: string[] = [];
    const mailingName = (record.RAMailingName ?? '').trim();
    const street1 = (record.Street1 ?? '').trim();
    const city = (record.City ?? '').trim();
    const state = (record.State ?? '').trim();
    const zipCode = (record.ZipCode ?? '').trim();
    const zipPlus4 = (record.ZipPlus4 ?? '').trim();
    const taxId = (record.RecoveryAgentTaxID ?? '').trim();
    const officeKey = (record.OfficeKey ?? '').trim();
    const opCompanyId = this.toNullablePositiveNumber(record.OpCompanyID);

    if (!mailingName) errors.push('Mailing Name is required field.');
    if (mailingName.length > 0 && mailingName.length < 2) errors.push('MailingName must contain at least 2 characters.');
    if (mailingName.length > 0 && !/^[A-Za-z0-9 ,&@#\/;:'"-]+$/.test(mailingName)) errors.push('MailingName has invalid special characters.');
    if (!street1) errors.push('Street1 is required field.');
    if (!city) errors.push('City is required field.');
    if (city.length > 0 && !/^[A-Za-z ,&_'.@#\/;:-]+$/.test(city)) errors.push('City may contain only alphabetic, space and allowed special characters.No numeric characters allowed.');
    if (!state) errors.push('State is required field.');
    if (!zipCode) errors.push('Zip is required field.');
    else if (!/^\d{5}$/.test(zipCode)) errors.push('ZipCode must be 5 digits and contains only numbers.');
    if (zipPlus4 && !/^\d{4}$/.test(zipPlus4)) errors.push('Zip Plus4 must be 4 digits and contains only numbers.');
    if (taxId && !/^\d{9}$/.test(taxId)) errors.push('Tax ID must be 9 digits.');
    if (opCompanyId === null && !officeKey) errors.push('Either OPco or OfficeKey is required.');
    if (opCompanyId !== null && officeKey) errors.push('Cannot have both OPco and OfficeKey.');
    return errors;
  }

  private initializeControlValues(): void {
    const selectedOperatingCompany = this.data.operatingCompanyOptions.find((option) =>
      option.id === this.toNullablePositiveNumber(this.formRecord.OpCompanyID)
    );
    const selectedOfficeKey = this.data.officeKeyOptions.find((option) => option.key === (this.formRecord.OfficeKey ?? ''));
    const selectedState = this.data.stateOptions.find((option) => option.code === (this.formRecord.State ?? ''));
    const selectedExternalOrg = this.externalOrgOptions.find((option) => option.value === (this.formRecord.ExternalOrg ?? ''));
    const selectedPaperlessIndicator = this.data.isEdit
      ? this.paperlessIndicatorOptions.find((option) => option.value === ((this.formRecord.PaperlessInd || null) as 'Y' | 'N' | null))
      : undefined;

    if (selectedOperatingCompany) {
      this.operatingCompanyControl.setValue(selectedOperatingCompany, { emitEvent: false });
    }

    if (selectedOfficeKey) {
      this.officeKeyControl.setValue(selectedOfficeKey, { emitEvent: false });
    }

    if (selectedState) {
      this.stateControl.setValue(selectedState, { emitEvent: false });
    }

    if (selectedExternalOrg) {
      this.externalOrgControl.setValue(selectedExternalOrg, { emitEvent: false });
    }

    if (this.data.isEdit && selectedPaperlessIndicator) {
      this.paperlessIndicatorControl.setValue(selectedPaperlessIndicator, { emitEvent: false });
    }
  }

  private filterOperatingCompanies(value: string | OperatingCompanyOption | null): OperatingCompanyOption[] {
    const filterValue = this.normalizeAutocompleteValue(value, (option) => option.label);

    if (!filterValue) {
      return this.data.operatingCompanyOptions.slice();
    }

    return this.data.operatingCompanyOptions.filter((option) =>
      option.label.toLowerCase().includes(filterValue) || option.name.toLowerCase().includes(filterValue)
    );
  }

  private filterOfficeKeys(value: string | OfficeKeyOption | null): OfficeKeyOption[] {
    const filterValue = this.normalizeAutocompleteValue(value, (option) => option.label);

    if (!filterValue) {
      return this.data.officeKeyOptions.slice();
    }

    return this.data.officeKeyOptions.filter((option) =>
      option.label.toLowerCase().includes(filterValue) || option.key.toLowerCase().includes(filterValue)
    );
  }

  private filterStates(value: string | StateOption | null): StateOption[] {
    const filterValue = this.normalizeAutocompleteValue(value, (option) => option.label);

    if (!filterValue) {
      return this.data.stateOptions.slice();
    }

    return this.data.stateOptions.filter((option) =>
      option.label.toLowerCase().includes(filterValue) || option.code.toLowerCase().includes(filterValue)
    );
  }

  private filterExternalOrgOptions(value: string | ExternalOrgOption | null): ExternalOrgOption[] {
    const filterValue = this.normalizeAutocompleteValue(value, (option) => option.label);

    if (!filterValue) {
      return this.externalOrgOptions.slice();
    }

    return this.externalOrgOptions.filter((option) => option.label.toLowerCase().includes(filterValue));
  }

  private filterPaperlessIndicatorOptions(
    value: string | PaperlessIndicatorOption | null
  ): PaperlessIndicatorOption[] {
    const filterValue = this.normalizeAutocompleteValue(value, (option) => option.label);

    if (!filterValue) {
      return this.paperlessIndicatorOptions.slice();
    }

    return this.paperlessIndicatorOptions.filter((option) =>
      option.label.toLowerCase().includes(filterValue)
    );
  }

  private normalizeAutocompleteValue<T>(
    value: string | T | null,
    getText: (option: T) => string
  ): string {
    if (!value) {
      return '';
    }

    return (typeof value === 'string' ? value : getText(value)).trim().toLowerCase();
  }

  private toNullableNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    const converted = Number(value);
    return Number.isFinite(converted) ? converted : null;
  }

  private toNullablePositiveNumber(value: unknown): number | null {
    const converted = this.toNullableNumber(value);
    return converted !== null && converted > 0 ? converted : null;
  }
}
